<script setup>
import { NFlex } from 'naive-ui';
import { computed } from 'vue';

const SPLAN_FULL_NAME = 'Student diagnostic Program for Literacy And Numeracy';
const SPLAN_SHORT_NAME = 'S-PLAN';
const SPLAN_HEAD_TITLE = '문해력·수리력 진단검사(S-PLAN) 안내';

const props = defineProps({
  reportType: {
    type: String,
    required: true,
    validator: (value) => ['student', 'teacher', 'principal'].includes(value),
  },
  reportData: {
    type: Object,
    default: () => ({ 
      schoolName: '',
      studentName: '',
      grade: '',
      className: '',
      studentNumber: '',
      gender: '',
      region: '', 
      reportSchoolType: '', 
    }),
  },
});

const mainTitle = computed(() => {
  let typeText = '';
  switch (props.reportType) {
    case 'student':
      typeText = '';
      break;
    case 'teacher':
      typeText = '(학급용)';
      break;
    case 'principal':
      typeText = '(학교장용)';
      break;
    default:
      typeText = ''; 
  }
  return `${props.reportData.region} 학생 문해력·수리력<br>진단검사(${SPLAN_SHORT_NAME}) 결과지 ${typeText}`;
});

const sPlanHeadTitle = computed(() => { 
  return `${props.reportData.region} 학생 ${SPLAN_HEAD_TITLE}`;
});

const studentInfoLine = computed(() => {
  let info = '';
  if (props.reportData.grade) info += props.reportData.grade +` 학년`;
  if (props.reportData.className) info += ` ${props.reportData.className} 반`;
  if (props.reportData.studentNumber) info += ` ${props.reportData.studentNumber} 번`;
  if (props.reportData.gender) info += ` 성별 : ${props.reportData.gender}`;
  return info.trim();
});

const principalGradeInfo = computed(() => {
    if (props.reportType === 'principal') {
        if (props.reportData.reportSchoolType === 'elementary') {
            // 초등학교는 실제 데이터에 따라 동적으로 표시 (배열 또는 문자열 처리)
            if (props.reportData.grade) {
                const grades = Array.isArray(props.reportData.grade) ? props.reportData.grade : [props.reportData.grade];
                if (grades.length > 0) {
                    const gradeText = grades.sort((a, b) => a - b).join(', ');
                    return `초 ${gradeText} 학년`;
                }
            }
            return ''; // grade 데이터가 없으면 빈 문자열
        } else if (props.reportData.reportSchoolType === 'middle' && props.reportData.grade) {
            const gradeValue = Array.isArray(props.reportData.grade) ? props.reportData.grade[0] : props.reportData.grade;
            return `중 ${gradeValue} 학년`; 
        } else if (props.reportData.reportSchoolType === 'high' && props.reportData.grade) {
            const gradeValue = Array.isArray(props.reportData.grade) ? props.reportData.grade[0] : props.reportData.grade;
            return `고 ${gradeValue} 학년`; 
        }
    }
    return ''; // 조건에 맞지 않으면 빈 문자열 반환
});
</script>

<template>  
    <h3 class="font-visual text-[40px] font-semibold text-center" v-html="mainTitle"></h3>
    <div :class="`info ${props.reportType}`">
      <p>{{ props.reportData.schoolName }}</p>     
      <template v-if="props.reportType === 'teacher'">       
        <p v-if="props.reportData.grade || props.reportData.className">
          {{ props.reportData.grade }}  학년  {{ props.reportData.className }}  반
        </p>
      </template>
      <template v-else-if="props.reportType === 'student'">        
        <p v-if="studentInfoLine">
          {{ studentInfoLine }}
        </p>        
        <p v-if="props.reportData.studentName">
          이름 : {{ props.reportData.studentName }}
        </p>
      </template>
      <template v-else-if="props.reportType === 'principal'">      
         <p v-if="principalGradeInfo">{{ principalGradeInfo }}</p>
      </template>
    </div>

    <div class="contents-plan">
        <div class="s-plan-head">
        <h4>{{ sPlanHeadTitle }}</h4>
        <p><span>* S-PLAN</span> : <span>S</span>tudent diagnostic <span>P</span>rogram for <span>L</span>iteracy <span>A</span>nd <span>N</span>umeracy</p>
        </div>

        <div class="s-plan-table pb-5 text-[20px] leading-[1.4]">
        <div class="row">
            <p>검사 목적</p>
            <p>{{ props.reportData.region }} 학생 문해력·수리력 진단검사는 학습의 기초가 되는 기초 소양으로서의 문해력과 수리력의 수준을 진단하기 위한 검사입니다.</p>
        </div>
        <div class="row">
            <p>대상 학년</p>
            <p>초4, 초6, 중2, 고1 학생을 대상으로 시행하며, 해당 학년뿐만 아니라 이전 학년도의 교육과정도 검사에 반영됩니다.</p>
        </div>
        <div class="row">
            <p>검사 영역</p>
            <div class="test">
            <p>모든 학습의 기초가 되는 기초 소양인 문해력과 수리력을 검사합니다.</p>
            <n-flex :wrap="false" :size="8" align="center">
                <p class="literacy-color">문해력</p>
                <p>학습과 삶의 맥락에서 다양한 내용과 형식의 글을 정확하게 이해하고 효과적으로 맥락에 맞게 표현하는 능력</p>
            </n-flex>
            <n-flex :wrap="false" :size="8" align="center">
                <p class="numeracy-color">수리력</p>
                <p>학습과 일상을 포함한 다양한 상황에서 수학의 내용과 방법을 활용하여 추론, 의사소통, 문제를 해결하는 능력</p>
            </n-flex>
            </div>
        </div>
        <div class="row">
            <p>검사 점수</p>
            <p>학생이 받는 점수의 전체 범위는 1,000점~2,000점이며, 이는 각 학년별 검사를 하나의 척도로 연결한 것으로 학령기 전체에서의 수준을 하나의 척도 점수로 나타낸 것입니다. <br><strong class="font-semibold">척도 점수는 학년 및 연도 간 비교가 가능하며 학생이 학년에 따라 받을 수 있는 점수의 범위는 다릅니다.<br />(초4: 1000점~1700점, 초6: 1050점~1800점, 중2: 1100점~1900점, 고1: 1150점~2000점)</strong></p>
        </div>
        <div class="row">
            <p>성취 수준</p>
            <p>각 학년 내에서 성취 정도에 따라 4개의 수준으로 구분됩니다.<br><strong class="font-semibold">4수준이 가장 높은 수준을 나타내며, 1수준은 가장 낮은 수준입니다.</strong><br>1수준의 경우 기초 문해력 및 기초 수리력이 부족하여 보완이 필요합니다.<br>보완이 필요한 영역은 하위 영역별로 확인할 수 있습니다.</p>
        </div>
        <div class="row">
            <p>검사 특징</p>
            <p>학습의 기본이 되는 역량인 문해력과 수리력의 성취 수준을 알려줍니다. 또한 학생이 학년이 바뀌어 다시 이 검사를 치르게 된다면, 이전 결과와 비교하여 어느 정도 성장했는지 알 수 있습니다.</p>
        </div>
        <div class="row">
            <p>검사 결과 활용</p>
            <p>진단 결과를 바탕으로 학생의 문해력, 수리력 영역에서의 강점과 추가로 도움이 필요한 영역을 파악할 수 있습니다. 또한 학교의 교육활동 및 개별 학습 활동의 방향과 내용을 결정하는 데 도움을 줄 수 있습니다.</p>
        </div>
        <div class="row text-[22px] notice-text">
          <strong class="font-semibold"> 점수는 문해력(Ⅰ, Ⅱ), 수리력(Ⅰ, Ⅱ)을 모두 응시한 경우를 기준으로 산출됩니다. 따라서 각 영역의 일부 차시만 응시했을 경우,<br />그 차시에 응답한 문항만 점수에 반영되어 학생의 점수가 낮게 나타납니다.</strong>
        </div>
        </div>
    </div>

    
</template>

<style lang="scss" scoped>
  h3 {letter-spacing: 2px;}
 .s-plan-head {
    text-align: center;   
      h4 {
        background-color: var(--result-green);      
        font-family: var(--smoe-font-visual);
        color: #fff;         
        font-weight: 600;
        font-size: 26px;
        border-radius: 10px;
        padding: 14px;
        letter-spacing: 2px;
      } 
      p {
        font-size: 24px;
        padding: 28px 0;
        line-height: 1;
      }
      p span {
        font-weight: 700;
      } 
      p span {color: var(--result-green);}      
  }
  .info {
    font-size: 22px;
    font-weight: 500;
    padding: 35px;
    gap: 10px;
    display: flex;
    flex-direction: column;
    min-width: 480px;
    text-align: center;
    letter-spacing: 3px;
    max-width: 165px;
    p {
      border: 1px solid #999;
      border-radius: 10px;
      padding: 10px 0;
      line-height: 1.3;      
      &:nth-child(1) {
        letter-spacing: 0;
      }
    }  
    &.student {
      padding: 14px 40px 20px;
    }
  }

  .s-plan-table {  
    .row {
      display: flex;
      padding: 14px 0;
      position: relative;
      &::before {
        content: ""; 
        background-image: url('@/assets/images/result/line.png');
        position: absolute;
        top: 0;
        width: 100%;
        height: 1px;
      }
      &.notice-text {
        margin-top: 5px;
        padding-left: 20px;
        background-color: #E0FFF0;
        border-radius: 15px;
        
        &::before {
          content: ""; 
          background-image: url('@/assets/images/result/line.png');
          background-repeat: repeat-x;
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 1px;
          z-index: 1;
        }
        &::after {
          content: "※";
          position: absolute;
          left: 0;
          top: 14px;
          z-index: 2;
        }
      }
      > p:nth-child(1) {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #F5F5F5;
        border-radius: 10px;     
        font-weight: 500;   
      }
      > p:nth-child(2), .test {
        flex: 4;
        padding:  14px 10px 14px 20px;
      }           
      .test {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
        .n-flex {
          p:nth-child(1) {
            width: 80px;
            height: 40px;
            display: flex;
            justify-content: center;
            align-items: center;
            border: 1px solid;
            border-radius: 50px;
            font-size: 16px;
          }
        }
      }
    }      
  }
</style>